import React, { useCallback, useEffect, useRef } from "react";
import { CheckboxV2 } from "@shopflo/ui";
import { useLocale } from "lib/hooks/useLocale";
import { useUserContext } from "lib/contexts/UserProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { putRequest } from "lib/core/apiClient";

interface MarketingConsentProps {
  customClass?: string;
  variant?: "default" | "compact";
}

// Local prop facade: checkout-ui doesn't have @radix-ui/react-checkbox as a direct dep, so CheckboxV2's Radix types don't resolve here.
type CompactCheckboxProps = {
  size?: "sm" | "md";
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
};
const CompactCheckbox = CheckboxV2 as unknown as React.ComponentType<CompactCheckboxProps>;

const MarketingConsent: React.FC<MarketingConsentProps> = ({ customClass, variant = "default" }) => {
  const { t } = useLocale();
  const {
    state: { user },
    actions: { setMarketingConsent },
  } = useUserContext();
  const {
    state: {
      merchant,
      checkoutMetadata,
      cartMetadata,
      ssoMetadata,
      consentState: { isConsentEnabled },
    },
  } = useMerchantContext();

  const enableMarketingFlag =
    checkoutMetadata?.uiAttributes?.layout?.enableMarketingFlag ??
    cartMetadata?.uiAttributes?.layout?.enableMarketingFlag ??
    ssoMetadata?.layout?.enable_marketing_flag ??
    true;
  const initialConsentHandled = useRef(false);
  const timer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // With DPDPA consent enforced, marketing consent comes from the consent UI;
    // no automatic opt-in here.
    if (isConsentEnabled) return;
    if (initialConsentHandled.current) return;

    if (merchant?.disableMarketingConsent) {
      setMarketingConsent(false);
      return;
    }

    if (!user?.marketing_consent) {
      handleMarketingConsent(true);
    }
  }, [user?.marketing_consent, merchant?.disableMarketingConsent, isConsentEnabled]);

  const handleMarketingConsent = async (consent: boolean) => {
    initialConsentHandled.current = true;
    setMarketingConsent(consent);
    if (!user?.uid) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      await putRequest("/attributes/v1/account-attributes", {
        attributes: {
          marketing_consent: consent,
        },
      });
    }, 1000);
  };

  const renderSalesCommsContext = useCallback(() => {
    const marketingText =
      checkoutMetadata?.uiAttributes?.marketingConsentText ||
      cartMetadata?.uiAttributes?.marketingConsentText ||
      ssoMetadata?.marketing_consent_text ||
      merchant?.marketingContextText;
    if (Boolean(marketingText)) return marketingText;
    switch (merchant?.salesCommsContext) {
      case 1: {
        return t("marketing_user_consent");
      }
      case 2: {
        return t("marketing_user_consent_v2");
      }
      case 3: {
        return t("marketing_user_consent_v3");
      }
      default: {
        return t("marketing_user_consent");
      }
    }
  }, [
    merchant?.salesCommsContext,
    merchant?.marketingContextText,
    checkoutMetadata?.uiAttributes?.marketingConsentText,
    cartMetadata?.uiAttributes?.marketingConsentText,
    ssoMetadata?.marketing_consent_text,
    t,
  ]);

  if (isConsentEnabled || !enableMarketingFlag) {
    return <></>;
  }

  if (variant === "compact") {
    const compactTopPadding = ssoMetadata?.popup?.card?.enabled === false ? "pt-4" : "pt-2";

    return (
      <div
        className={`flex w-full flex-row items-center justify-center text-left space-x-2 ${compactTopPadding} ${
          customClass ?? ""
        }`}>
        <CompactCheckbox
          size="sm"
          checked={Boolean(user?.marketing_consent)}
          onCheckedChange={(checked: boolean) => {
            handleMarketingConsent(Boolean(checked));
          }}
        />
        <label
          onClick={() => handleMarketingConsent(!Boolean(user?.marketing_consent))}
          className="select-none text-text-xs-medium text-coal-light cursor-pointer"
          dangerouslySetInnerHTML={{ __html: renderSalesCommsContext() ?? "" }}
        />
      </div>
    );
  }

  return (
    <div
      className={`flex h-12 w-full flex-row items-center justify-center space-x-2 px-6 xxs:px-4 pt-7 ${
        customClass ?? ""
      }`}>
      <input
        type="checkbox"
        id="marketing_user_consent"
        name="marketing_user_consent"
        className="h-[14px] w-[14px] cursor-pointer rounded-sm accent-coal-dark"
        checked={Boolean(user?.marketing_consent)}
        onClick={() => {
          handleMarketingConsent(!user?.marketing_consent);
        }}
      />
      <label
        htmlFor="marketing_user_consent"
        className="select-none text-xs font-normal text-coal-light cursor-pointer">
        <p dangerouslySetInnerHTML={{ __html: renderSalesCommsContext() ?? "" }} />
      </label>
    </div>
  );
};

export default React.memo(MarketingConsent);
